const INSTORE_KEY = 'instore';
const VALID_MODES = ['on', 'styling'];

function getCookieDomain() {
  const hostname = window.location.hostname;
  if (hostname.includes('birdsnest.com.au')) {
    return '; domain=.birdsnest.com.au';
  }
  return '';
}

function getInstoreCookie() {
  const match = document.cookie.match(/(?:^|;\s*)instore=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function setInstoreCookie(value) {
  const domain = getCookieDomain();
  document.cookie = `instore=${encodeURIComponent(value)}; path=/; max-age=2592000${domain}; SameSite=Lax`;
}

function removeInstoreCookie() {
  const domain = getCookieDomain();
  document.cookie = `instore=; path=/; max-age=0${domain}; SameSite=Lax`;
}

export function getInstoreMode() {
  const cookieVal = getInstoreCookie();
  if (VALID_MODES.includes(cookieVal)) return cookieVal;
  const localVal = localStorage.getItem(INSTORE_KEY);
  if (VALID_MODES.includes(localVal)) return localVal;
  return null;
}

let loginLinksListenerAttached = false;

function preserveInstoreOnLoginLinks() {
  const mode = getInstoreMode();
  if (!mode) return;

  const updateUrlWithInstore = (rawUrl) => {
    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.pathname.includes('/logout')) return rawUrl;

      const currentReturnTo = url.searchParams.get('return_to');
      if (currentReturnTo) {
        const returnUrl = new URL(currentReturnTo, window.location.origin);
        returnUrl.searchParams.set('instore', mode);
        url.searchParams.set(
          'return_to',
          returnUrl.pathname + returnUrl.search
        );
      } else {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('instore', mode);
        url.searchParams.set(
          'return_to',
          currentUrl.pathname + currentUrl.search
        );
      }
      return url.href;
    } catch {
      return rawUrl;
    }
  };

  const loginLinks = document.querySelectorAll(
    'a[href*="/account"], a[href*="/customer_authentication/login"], a#customer_login_link'
  );
  loginLinks.forEach((link) => {
    link.href = updateUrlWithInstore(link.href);
  });

  if (!loginLinksListenerAttached) {
    loginLinksListenerAttached = true;
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest(
          'a[href*="/account"], a[href*="/customer_authentication/login"], a#customer_login_link'
        );
        if (!link) return;
        const currentMode = getInstoreMode();
        if (!currentMode) return;
        link.href = updateUrlWithInstore(link.href);
      },
      true
    );
  }
}

/**
 * Sets the in-store fitting room mode based on URL parameters, cookie, or localStorage
 * Supports 'on', 'off', and 'styling' values via 'instore' query parameter
 */
export function setInstoreFittingRoomMode() {
  const instoreParam = new URL(window.location.href).searchParams.get(
    'instore'
  );

  if (instoreParam === 'off') {
    localStorage.removeItem(INSTORE_KEY);
    removeInstoreCookie();
    return;
  }

  if (VALID_MODES.includes(instoreParam)) {
    localStorage.setItem(INSTORE_KEY, instoreParam);
    setInstoreCookie(instoreParam);
  } else {
    const activeMode = getInstoreMode();
    if (activeMode) {
      localStorage.setItem(INSTORE_KEY, activeMode);
      setInstoreCookie(activeMode);
    } else {
      return;
    }
  }

  preserveInstoreOnLoginLinks();
  initFittingRoomButtons();
}

/**
 * Initializes fitting room buttons by attaching click event listeners
 * to all elements with the "data-add-to-fitting-room" attribute
 */
export function initFittingRoomButtons() {
  const fittingRoomButtons = document.querySelectorAll(
    '[data-add-to-fitting-room]'
  );
  let activeFittingRoomTriggerButton = null;

  if (fittingRoomButtons.length === 0) {
    return;
  }

  // Check instore mode: show button and the appropriate label
  const instoreMode = getInstoreMode();

  if (instoreMode === 'on' || instoreMode === 'styling') {
    fittingRoomButtons.forEach((button) => {
      button.classList.remove('visually-hidden');
    });
  }
  // If instore is "on", hide the "Add to bag" button on the PDP
  if (instoreMode === 'on') {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      if (btn.closest('[data-product-form]')) {
        btn.style.display = 'none';
      }
    });
  }
  // If instore is "on", show fitting-room-label (remove visually-hidden)
  if (instoreMode === 'on') {
    document.querySelectorAll('.fitting-room-label').forEach((el) => {
      el.classList.remove('visually-hidden');
    });
  }
  // If instore is "styling", show nest-lounge-label (remove visually-hidden)
  if (instoreMode === 'styling') {
    document.querySelectorAll('.nest-lounge-label').forEach((el) => {
      el.classList.remove('visually-hidden');
    });
  }

  // Wait for custom element to be defined
  const getModal = () => {
    return document.querySelector('mr-modal#fitting-room-popup');
  };

  // Check if custom element is already defined
  let modal = getModal();

  if (!modal) {
    // Wait for custom element to be registered
    customElements.whenDefined('mr-modal').then(() => {
      modal = getModal();
      if (modal) {
        attachButtonListeners(modal);
        initFittingRoomSaveButton(modal, () => activeFittingRoomTriggerButton);
      }
    });
  } else {
    attachButtonListeners(modal);
    initFittingRoomSaveButton(modal, () => activeFittingRoomTriggerButton);
  }

  function attachButtonListeners(modalElement) {
    fittingRoomButtons.forEach((button) => {
      storeDefaultFittingRoomSaveLabels(button);

      // Re-enable the button on variant change since a successful add disables it
      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
        window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, () => {
          resetFittingRoomSaveButton(button);
        });
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (!hasSelectedVariant()) {
          document
            .querySelector('variant-selects')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        activeFittingRoomTriggerButton = button;
        updateFittingRoomPopupDetails(modalElement);
        resetFittingRoomPopupState(modalElement, button);
        // Use the custom element's show() method
        if (typeof modalElement.show === 'function') {
          modalElement.show();
        }
      });
    });
  }
}

/**
 * Restores the save button and trigger button labels, clearing leftover feedback.
 */
function resetFittingRoomPopupState(modalElement, triggerButton) {
  const saveButton = modalElement.querySelector('[data-fitting-room-save]');
  const errorContainer = modalElement.querySelector(
    '[data-cart-errors-container]'
  );

  resetFittingRoomSaveButton(saveButton);
  resetFittingRoomSaveButton(triggerButton);
  hideError(errorContainer);
}

function storeDefaultFittingRoomSaveLabels(saveButton) {
  const labels = saveButton.querySelectorAll(
    '.fitting-room-label, .nest-lounge-label'
  );

  labels.forEach((label) => {
    if (!label.dataset.defaultText) {
      label.dataset.defaultText = label.textContent;
    }
  });
}

function resetFittingRoomSaveButton(saveButton) {
  if (!saveButton) return;

  saveButton.classList.remove('is-success');
  delete saveButton.dataset.fittingRoomAdded;
  saveButton.disabled = false;

  const instoreMode = getInstoreMode();
  const activeLabelSelector =
    instoreMode === 'on' ? '.fitting-room-label' : '.nest-lounge-label';
  const labels = saveButton.querySelectorAll(
    '.fitting-room-label, .nest-lounge-label'
  );

  labels.forEach((label) => {
    if (label.dataset.defaultText) {
      label.textContent = label.dataset.defaultText;
    }
    label.classList.toggle(
      'visually-hidden',
      !label.matches(activeLabelSelector)
    );
  });
}

function showFittingRoomButtonSuccess(button, instoreMode) {
  if (!button) return;

  const labels = button.querySelectorAll(
    '.fitting-room-label, .nest-lounge-label'
  );
  const successLabel = button.querySelector(
    instoreMode === 'on' ? '.fitting-room-label' : '.nest-lounge-label'
  );

  labels.forEach((label) => {
    label.classList.add('visually-hidden');
  });

  if (successLabel) {
    successLabel.textContent =
      instoreMode === 'on'
        ? 'This was added to your Changeroom'
        : 'This was added to your Styling Room';
    successLabel.classList.remove('visually-hidden');
  }

  button.classList.add('is-success');
  button.dataset.fittingRoomAdded = 'true';
  button.disabled = true;
}

/**
 * Checks whether the customer has explicitly selected a variant (e.g. a size).
 */
function hasSelectedVariant() {
  try {
    const selectedVariantScript = document.querySelector(
      'variant-selects [data-selected-variant]'
    );
    if (!selectedVariantScript) return false;

    const variantData = JSON.parse(selectedVariantScript.textContent || '{}');
    return Boolean(variantData && variantData.id && variantData.sku);
  } catch (error) {
    console.error('Error checking selected variant:', error);
    return false;
  }
}

/**
 * Updates fitting room popup details based on the currently selected variant
 */
function updateFittingRoomPopupDetails(modalElement) {
  try {
    const selectedVariantScript = document.querySelector(
      'variant-selects [data-selected-variant]'
    );

    if (!selectedVariantScript) return;

    const variantData = JSON.parse(selectedVariantScript.textContent || '{}');
    if (!variantData || typeof variantData.price !== 'number') return;

    const productDetails = modalElement.querySelector(
      '[data-fitting-room-product]'
    );
    const priceElement = modalElement.querySelector(
      '.fitting-room-popup__product-price'
    );
    const optionValueElement = modalElement.querySelector(
      '.fitting-room-popup__option-value'
    );

    if (productDetails && variantData.id && variantData.sku) {
      productDetails.setAttribute('data-variant-id', variantData.id);
      productDetails.setAttribute('data-variant-sku', variantData.sku);
    }

    if (priceElement) {
      const price = variantData.price / 100;
      const compareAtPrice =
        typeof variantData.compare_at_price === 'number'
          ? variantData.compare_at_price
          : null;

      const formattedPrice = `$${price.toFixed(2)}`;

      if (compareAtPrice && compareAtPrice > variantData.price) {
        const formattedCompareAtPrice = `$${(compareAtPrice / 100).toFixed(2)}`;

        priceElement.innerHTML = `
        <span data-product-price class="product__price--regular product__price--sale">${formattedPrice}</span>
        <s data-compare-price class="product__price--compare">${formattedCompareAtPrice}</s>`;
      } else {
        priceElement.innerHTML = `<span data-product-price class="product__price--regular product__price--sale">${formattedPrice}</span>`;
      }
    }

    if (optionValueElement) {
      optionValueElement.textContent = variantData.public_title || '';
    }
  } catch (error) {
    console.error('Error updating fitting room variant details:', error);
  }
}

/**
 * Initializes the save button functionality for fitting room popup
 */
function initFittingRoomSaveButton(modalElement, getActiveTriggerButton) {
  const saveButton = modalElement.querySelector('[data-fitting-room-save]');
  const continueButton = modalElement.querySelector(
    '[data-fitting-room-continue]'
  );
  const nameInput = modalElement.querySelector('#fitting-room-name');
  const errorContainer = modalElement.querySelector(
    '[data-cart-errors-container]'
  );
  const productDetails = modalElement.querySelector(
    '[data-fitting-room-product]'
  );

  if (!saveButton || !nameInput || !errorContainer) {
    return;
  }

  storeDefaultFittingRoomSaveLabels(saveButton);

  // Handle save button click
  saveButton.addEventListener('click', async (event) => {
    event.preventDefault();

    if (saveButton.dataset.fittingRoomAdded === 'true') {
      return;
    }

    // Clear previous errors
    hideError(errorContainer);

    // Get input value and trim whitespace
    const buyerName = nameInput.value.trim();

    // Validate name input
    if (!buyerName) {
      showError(errorContainer, 'Please enter your name');
      return;
    }

    // Get variant SKU from product details
    const variantSku = productDetails?.getAttribute('data-variant-sku') || '';

    if (!variantSku) {
      showError(errorContainer, 'Product SKU is missing. Please try again.');
      return;
    }

    const productHandle =
      productDetails?.getAttribute('data-product-handle') || '';

    if (!productHandle) {
      showError(errorContainer, 'Product handle is missing. Please try again.');
      return;
    }

    const instoreMode = getInstoreMode();

    // Show loading state
    saveButton.classList.add('is-loading');
    saveButton.disabled = true;

    try {
      // Prepare payload
      const payload = {
        buyer_name: buyerName,
        fitting_room_id: instoreMode === 'on' ? 'Fitting Room' : 'Nest Lounge',
        lines: [
          {
            sku: variantSku,
            qty: 1,
            handle: productHandle,
          },
        ],
      };

      const apiBaseUrl = process.env.API_BASE_URL;
      const fittingRoomApiUrl = `${apiBaseUrl}/public/v1/fitting-room/order`;

      const response = await fetch(fittingRoomApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();

      hideError(errorContainer);
      showFittingRoomButtonSuccess(saveButton, instoreMode);
      showFittingRoomButtonSuccess(getActiveTriggerButton?.(), instoreMode);
    } catch (error) {
      console.error('Error saving to fitting room:', error);
      showError(errorContainer, 'Failed to save. Please try again.');
    } finally {
      // Remove loading state
      saveButton.classList.remove('is-loading');
      if (saveButton.dataset.fittingRoomAdded !== 'true') {
        saveButton.disabled = false;
      }
    }
  });

  // Handle continue shopping button
  if (continueButton) {
    continueButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (typeof modalElement.hide === 'function') {
        modalElement.hide();
      }
    });
  }
}

/**
 * Shows error message in the error container
 */
function showError(container, message) {
  if (!container) return;

  const closeErrorButton = `
    <button type="button" class="errors__close" aria-label="Close">
      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 192 192"><path d="M150 42 42 150M150 150 42 42" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
  `;

  container.innerHTML = `
    <div class="errors" data-error autofocus>
      ${message}
      ${closeErrorButton}
    </div>
  `;

  container.classList.add('is-visible');

  // Handle close button
  const closeBtn = container.querySelector('.errors__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      hideError(container);
    });
  }

  // Scroll to error
  container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * Hides error message
 */
function hideError(container) {
  if (!container) return;
  container.classList.remove('is-visible');
  container.innerHTML = '';
}
